import { router } from '@inertiajs/react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Save, X, RotateCcw, FileJson } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import type { InstanceConfigBackups } from '../types'

interface Props {
  worker: { id: string }
  config: Record<string, unknown> | null
  backups: InstanceConfigBackups | null
}

const HTML_ESCAPE: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;' }

interface ConfirmationDialogState {
  title: string
  description: string
  confirmLabel: string
  onConfirm: () => void
}

function highlightJsonString(text: string): string {
  if (!text) return ''
  const escaped = text.replace(/[&<>]/g, (c) => HTML_ESCAPE[c]!)
  return escaped.replace(
    /("(?:\\.|[^"\\])*"\s*:)|("(?:\\.|[^"\\])*")|(\b(?:true|false)\b)|(\bnull\b)|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g,
    (match, key, str, bool, nul, num) => {
      let cls = ''
      if (key) cls = 'text-sky-700 dark:text-sky-400'
      else if (str) cls = 'text-emerald-700 dark:text-emerald-400'
      else if (bool) cls = 'text-violet-700 dark:text-violet-400'
      else if (nul) cls = 'text-muted-foreground italic'
      else if (num) cls = 'text-amber-700 dark:text-amber-400'
      return `<span class="${cls}">${match}</span>`
    },
  )
}

function LineGutter({ count }: { count: number }) {
  return (
    <div className="select-none text-right pr-3 pl-3 py-3 text-muted-foreground/70 tabular-nums">
      {Array.from({ length: count }, (_, i) => (
        <div key={i}>{i + 1}</div>
      ))}
    </div>
  )
}

function ReadOnlyView({ text }: { text: string }) {
  const lineCount = useMemo(() => Math.max(text.split('\n').length, 1), [text])
  const html = useMemo(() => highlightJsonString(text), [text])
  return (
    <div className="font-mono text-xs leading-6 bg-muted/30 rounded-md max-h-[500px] overflow-auto">
      <div className="flex min-w-fit">
        <div className="sticky left-0 bg-muted/60 border-r border-border z-10">
          <LineGutter count={lineCount} />
        </div>
        <pre
          className="m-0 px-3 py-3 whitespace-pre text-foreground"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </div>
    </div>
  )
}

function JsonEditor({
  value,
  onChange,
  height = 500,
}: {
  value: string
  onChange: (next: string) => void
  height?: number
}) {
  const taRef = useRef<HTMLTextAreaElement>(null)
  const preRef = useRef<HTMLPreElement>(null)
  const gutterRef = useRef<HTMLDivElement>(null)
  const lineCount = useMemo(() => Math.max(value.split('\n').length, 1), [value])
  const html = useMemo(() => highlightJsonString(value), [value])

  const sync = () => {
    const ta = taRef.current
    if (!ta) return
    const x = -ta.scrollLeft
    const y = -ta.scrollTop
    if (preRef.current) {
      preRef.current.style.transform = `translate3d(${x}px, ${y}px, 0)`
    }
    if (gutterRef.current) {
      gutterRef.current.style.transform = `translate3d(0, ${y}px, 0)`
    }
  }

  useEffect(() => {
    sync()
  }, [value])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault()
      const ta = e.currentTarget
      const start = ta.selectionStart
      const end = ta.selectionEnd
      const next = value.substring(0, start) + '  ' + value.substring(end)
      onChange(next)
      requestAnimationFrame(() => {
        if (taRef.current) {
          taRef.current.selectionStart = taRef.current.selectionEnd = start + 2
        }
      })
    }
  }

  return (
    <div
      className="relative font-mono text-xs leading-6 border border-input rounded-md overflow-hidden bg-background flex"
      style={{ height }}
    >
      <div className="relative flex-shrink-0 bg-muted/40 border-r border-border overflow-hidden w-12">
        <div
          ref={gutterRef}
          aria-hidden="true"
          className="absolute top-0 left-0 right-0 will-change-transform"
        >
          <LineGutter count={lineCount} />
        </div>
      </div>
      <div className="relative flex-1 h-full overflow-hidden">
        <pre
          ref={preRef}
          aria-hidden="true"
          className="absolute top-0 left-0 m-0 px-3 py-3 whitespace-pre pointer-events-none text-foreground will-change-transform"
          dangerouslySetInnerHTML={{ __html: html + '\n\n' }}
        />
        <textarea
          ref={taRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onScroll={sync}
          onKeyDown={handleKeyDown}
          spellCheck={false}
          wrap="off"
          className="absolute inset-0 px-3 py-3 bg-transparent text-transparent caret-foreground resize-none whitespace-pre focus:outline-none w-full h-full overflow-auto"
          style={{ caretColor: 'var(--foreground)' }}
        />
      </div>
    </div>
  )
}

export default function ConfigTab({ worker, config, backups }: Props) {
  const initialText = useMemo(() => (config ? JSON.stringify(config, null, 2) : ''), [config])
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(initialText)
  const [parseError, setParseError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [confirmationDialog, setConfirmationDialog] = useState<ConfirmationDialogState | null>(null)

  useEffect(() => {
    if (!editing) setDraft(initialText)
  }, [initialText, editing])

  const executeUpdate = () => {
    setSubmitting(true)
    router.put(
      `/cc/instances/${worker.id}/update_config`,
      { config_json: draft },
      {
        preserveScroll: true,
        onFinish: () => setSubmitting(false),
        onSuccess: () => setEditing(false),
      },
    )
  }

  const submitUpdate = () => {
    try {
      JSON.parse(draft)
    } catch (e) {
      setParseError(`JSON inválido: ${(e as Error).message}`)
      return
    }
    setParseError(null)
    setConfirmationDialog({
      title: 'Salvar openclaw.json',
      description: 'Sobrescrever openclaw.json desta instância? Um backup automático será criado antes.',
      confirmLabel: 'Salvar',
      onConfirm: executeUpdate,
    })
  }

  const restore = (filename: string) => {
    setConfirmationDialog({
      title: 'Restaurar backup',
      description: `Restaurar backup ${filename}? Um backup do estado atual é criado antes da restauração.`,
      confirmLabel: 'Restaurar',
      onConfirm: () => {
        router.post(`/cc/instances/${worker.id}/restore_config`, { filename }, { preserveScroll: true })
      },
    })
  }

  const cancelEdit = () => {
    setEditing(false)
    setDraft(initialText)
    setParseError(null)
  }

  return (
    <div className="space-y-4">
      <section className="bg-card border border-border rounded-lg overflow-hidden">
        <header className="flex items-center justify-between gap-3 px-4 py-3 bg-muted border-b border-border">
          <h3 className="text-sm font-semibold text-foreground inline-flex items-center gap-2">
            <FileJson className="h-4 w-4" /> openclaw.json
          </h3>
          <div className="flex gap-2">
            {!editing ? (
              <Button size="sm" variant="outline" onClick={() => setEditing(true)} disabled={!config}>
                Editar
              </Button>
            ) : (
              <>
                <Button size="sm" variant="ghost" onClick={cancelEdit}>
                  <X className="h-4 w-4 mr-1" /> Cancelar
                </Button>
                <Button size="sm" onClick={submitUpdate} disabled={submitting}>
                  <Save className="h-4 w-4 mr-1" />
                  {submitting ? 'Salvando…' : 'Salvar'}
                </Button>
              </>
            )}
          </div>
        </header>
        <div className="p-4 space-y-2">
          {!config && (
            <div className="text-center py-8">
              <FileJson className="h-8 w-8 text-muted-foreground/60 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Não foi possível ler o config do broker.</p>
            </div>
          )}
          {config && !editing && <ReadOnlyView text={initialText} />}
          {config && editing && (
            <>
              <JsonEditor value={draft} onChange={setDraft} />
              {parseError && (
                <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-500/20 rounded-md px-3 py-2">
                  {parseError}
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Secrets vêm como <code className="font-mono">[REDACTED]</code>. Ao salvar, esses placeholders são preservados com os valores reais. Substitua apenas quando quiser trocar o valor.
              </p>
            </>
          )}
        </div>
      </section>

      <section className="bg-card border border-border rounded-lg overflow-hidden">
        <header className="px-4 py-3 bg-muted border-b border-border">
          <h3 className="text-sm font-semibold text-foreground inline-flex items-center gap-2">
            <RotateCcw className="h-4 w-4" /> Backups
          </h3>
          {backups?.backup_dir && (
            <p className="text-xs text-muted-foreground mt-0.5 font-mono break-all">{backups.backup_dir}</p>
          )}
        </header>
        <div>
          {(!backups || backups.backups.length === 0) ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              Sem backups ainda. Cada escrita em <code className="font-mono">openclaw.json</code> cria um arquivo aqui.
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">Arquivo</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">Quando</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">Autor</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">Tamanho</th>
                  <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">Ação</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {backups.backups.map((b) => (
                  <tr key={b.filename} className="hover:bg-accent transition-colors">
                    <td className="px-4 py-2 font-mono text-xs break-all">{b.filename}</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">{b.created_at}</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">{b.actor || '—'}</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">{(b.size_bytes / 1024).toFixed(1)} KB</td>
                    <td className="px-4 py-2 text-right">
                      <Button size="sm" variant="outline" onClick={() => restore(b.filename)}>
                        Restaurar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
      <AlertDialog open={confirmationDialog !== null} onOpenChange={(open) => { if (!open) setConfirmationDialog(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmationDialog?.title || 'Confirmar'}</AlertDialogTitle>
            <AlertDialogDescription>{confirmationDialog?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmationDialog(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const onConfirm = confirmationDialog?.onConfirm
                setConfirmationDialog(null)
                onConfirm?.()
              }}
            >
              {confirmationDialog?.confirmLabel || 'OK'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
